import SaveIcon from "@mui/icons-material/Check";
import {
    default as CancelIcon,
    default as Close,
} from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    TextField,
    Typography,
} from "@vapor/react-material";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { GlobalLabelUpdateAction } from "../../../actions/labels";
import { OrderLabel } from "../../../interfaces/label";
import Button from "../../vapor/button/index";
import Tag from "../../vapor/tag";
import { TagColor } from "../../vapor/tag/colors";
import ConfirmActionAlert from "../modals/confirmAction";
import AddLabel from "./addLabel";

type ManageLabelsDialogProps = {
    labels: OrderLabel[]; // all unique labels used across documents
    onSave: (actions: Array<GlobalLabelUpdateAction>) => void;
    usedLabels: Array<string>;
};

export const ManageLabelsDialog: React.FC<ManageLabelsDialogProps> = ({
    labels,
    onSave,
    usedLabels,
}) => {
    const [open, setOpen] = useState(false);
    const [editingLabel, setEditingLabel] = useState<string | null>(null);
    const [deletingLabel, setDeletingLabel] = useState<string | null>(null);
    const [newLabel, setNewLabel] = useState("");
    const [openConfirmRename, setOpenConfirmRename] = useState(false);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

    // Internal state to manage dialog labels
    const [dialogLabels, setDialogLabels] = useState<OrderLabel[]>(labels);

    // Actions to update index document
    const [globalLabelActions, setGlobalLabelActions] = useState<
        Array<GlobalLabelUpdateAction>
    >([]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setEditingLabel(null);
        setNewLabel("");
        setGlobalLabelActions([]);
    };

    const handleDeleteLabel = (label: string) => {
        confirmAction(
            usedLabels.includes(label),
            () => _handleDeleteLabel(label),
            setOpenConfirmDelete,
        );
    };

    const _handleDeleteLabel = (label: string | null) => {
        if (!label) {
            console.error("_handleDeleteLabel input is null");
            return;
        }
        setGlobalLabelActions([
            ...globalLabelActions,
            {
                type: "delete",
                data: label,
            },
        ]);
        setDialogLabels(dialogLabels.filter((l) => l.name !== label));
        setDeletingLabel(null);
    };

    const handleStartEdit = (label: string) => {
        setEditingLabel(label);
        setNewLabel(label);
    };

    const handleSaveRename = (targetLabel: string) => {
        confirmAction(
            usedLabels.includes(targetLabel),
            () => _handleSaveRename(),
            setOpenConfirmRename,
        );
    };

    const _handleSaveRename = () => {
        if (editingLabel && newLabel.trim()) {
            setGlobalLabelActions([
                ...globalLabelActions,
                {
                    type: "rename",
                    data: {
                        oldLabelName: editingLabel,
                        newLabelName: newLabel.trim(),
                    },
                },
            ]);
            setDialogLabels(
                dialogLabels.map((l) =>
                    l.name === editingLabel
                        ? { ...l, name: newLabel.trim() }
                        : l,
                ),
            );
            setEditingLabel(null);
            setNewLabel("");
        }
    };

    const confirmAction = (
        condition: boolean,
        onConfirm: () => void,
        setConfirmOpen: (open: boolean) => void,
    ) => {
        if (condition) setConfirmOpen(true);
        else onConfirm();
    };

    useEffect(() => {
        setDialogLabels(labels);
    }, [labels]);

    const handleCancelEdit = () => {
        setEditingLabel(null);
        setDeletingLabel(null);
        setNewLabel("");
    };

    return (
        <>
            <Button kind="secondary" size="medium" onClick={handleOpen}>
                Gestisci Etichette
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        height: "80vh",
                        maxHeight: "80vh",
                    },
                }}
            >
                <DialogTitle>
                    Gestisci Etichette
                    <IconButton color="primary" onClick={() => handleClose()}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <AddLabel
                        presetLabels={labels.map((l) => l.name)}
                        addNewLabel={function (
                            name: string,
                            color: TagColor,
                        ): void {
                            const newOrderLabel = {
                                name: name,
                                color: color,
                            };
                            setGlobalLabelActions([
                                ...globalLabelActions,
                                {
                                    type: "create",
                                    data: newOrderLabel,
                                },
                            ]);
                            setDialogLabels([newOrderLabel, ...dialogLabels]);
                        }}
                    />
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{ marginTop: "8px" }}
                        gutterBottom
                    >
                        NB: Modificare o eliminare le etichette esistenti avrà
                        effetto su tutti i documenti associati a tali etichette.
                    </Typography>
                    {dialogLabels.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nessuna etichetta disponibile.
                        </Typography>
                    ) : (
                        <List>
                            {dialogLabels.map((label) => (
                                <ListItem
                                    key={label.name}
                                    sx={{
                                        "&:focus-visible": {
                                            border: "none",
                                            outline: "none",
                                        },
                                        "&:hover": {
                                            border: "none",
                                            outline: "none",
                                        },
                                    }}
                                >
                                    {editingLabel === label.name ? (
                                        <>
                                            <TextField
                                                value={newLabel}
                                                onChange={(e) =>
                                                    setNewLabel(e.target.value)
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleSaveRename(
                                                            label.name,
                                                        );
                                                    }
                                                    if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        setEditingLabel(null);
                                                    }
                                                }}
                                                size="small"
                                                sx={{ flex: 1, mr: 2 }}
                                            />
                                            <IconButton
                                                color="primary"
                                                onClick={() =>
                                                    handleSaveRename(label.name)
                                                }
                                                disabled={!newLabel.trim()}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton
                                                color="inherit"
                                                onClick={handleCancelEdit}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            {/*<ListItemText primary={label.name} />*/}
                                            <Tag
                                                color={label.color}
                                                key={
                                                    "order-label-" + label.name
                                                }
                                            >
                                                {label.name}
                                            </Tag>
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    color="primary"
                                                    onClick={() =>
                                                        handleStartEdit(
                                                            label.name,
                                                        )
                                                    }
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    color="error"
                                                    onClick={() => {
                                                        setDeletingLabel(
                                                            label.name,
                                                        );
                                                        handleDeleteLabel(
                                                            label.name,
                                                        );
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        kind="secondary"
                        size="medium"
                        onClick={() => {
                            onSave(globalLabelActions);
                            handleClose();
                        }}
                    >
                        <FormattedMessage id="actions.save" />
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmActionAlert
                open={openConfirmRename}
                title={"Conferma Rinomina Etichetta"}
                description={
                    "Hai 1 o più report a cui è associata questa etichetta. Vuoi continuare?"
                }
                onConfirm={function (): void {
                    _handleSaveRename();
                    setOpenConfirmRename(false);
                }}
                onCancel={function (): void {
                    setOpenConfirmRename(false);
                }}
            ></ConfirmActionAlert>
            <ConfirmActionAlert
                open={openConfirmDelete}
                title={"Conferma Cancellazione Etichetta"}
                description={
                    "Hai 1 o più report a cui è associata questa etichetta. Vuoi continuare?"
                }
                onConfirm={function (): void {
                    _handleDeleteLabel(deletingLabel);
                    setOpenConfirmDelete(false);
                }}
                onCancel={function (): void {
                    setDeletingLabel(null);
                    setOpenConfirmDelete(false);
                }}
            ></ConfirmActionAlert>
        </>
    );
};
